/**
 * Task Catalog: the organization's reusable work definitions.
 * Archive with is_active = false (never deleted). Title is unique per
 * organization, case/spacing-insensitive, archived rows included.
 */
import { supabase } from "../../supabase";
import { assertCan, CAPABILITY } from "../permissions";
import { resolveOrganizationIdForQueries } from "../orgQueryScope";
import { shouldSkipSupabase } from "../guestMode";
import {
  findCatalogDuplicate,
  normalizeTitle,
  validateTaskInput,
} from "./taskModel";

const EDIT_DENIED = "Only an organization admin can change the Task Catalog.";

export class TaskCatalogDuplicateError extends Error {
  constructor(existing) {
    super(
      existing?.is_active === false
        ? "A catalog task with this title is archived. Restore it instead."
        : "A catalog task with this title already exists.",
    );
    this.name = "TaskCatalogDuplicateError";
    this.existing = existing || null;
  }
}

async function requireOrg(actor) {
  const organizationId = await resolveOrganizationIdForQueries(actor, supabase);
  if (!organizationId) throw new Error("Organization is required.");
  return organizationId;
}

export async function loadTaskCatalog(actor, { includeArchived = true } = {}) {
  assertCan(actor, CAPABILITY.PROJECT_TASK_VIEW);
  if (shouldSkipSupabase(actor)) return [];
  const organizationId = await requireOrg(actor);
  let query = supabase
    .from("task_catalog")
    .select(
      "task_catalog_id, organization_id, title, description, planned_duration_minutes, is_active, created_at, updated_at",
    )
    .eq("organization_id", organizationId)
    .order("title", { ascending: true });
  if (!includeArchived) query = query.eq("is_active", true);
  const { data, error } = await query;
  if (error) throw error;
  return data || [];
}

function checkInput({ title, description, durationMinutes = null }) {
  const result = validateTaskInput({ title, description, durationMinutes });
  if (!result.ok) throw new Error(Object.values(result.errors)[0]);
}

/**
 * A catalog item (template) carries an optional planned duration in minutes,
 * never dates. Project tasks copy it at creation (snapshot, like the text).
 */
export async function createCatalogTask(actor, { title, description = "", durationMinutes = null }, { existing } = {}) {
  assertCan(actor, CAPABILITY.TASK_CATALOG_EDIT, {}, undefined, EDIT_DENIED);
  checkInput({ title, description, durationMinutes });
  const organizationId = await requireOrg(actor);
  const known = existing || (await loadTaskCatalog(actor));
  const duplicate = findCatalogDuplicate(known, title);
  if (duplicate) throw new TaskCatalogDuplicateError(duplicate);

  const { data, error } = await supabase
    .from("task_catalog")
    .insert({
      organization_id: organizationId,
      title: normalizeTitle(title),
      description: String(description || "").trim() || null,
      ...(durationMinutes ? { planned_duration_minutes: durationMinutes } : {}),
      is_active: true,
      created_by: actor?.supabase_user_id || null,
    })
    .select("*")
    .single();
  if (error) {
    // Lost a race with another admin: surface it as the same duplicate message.
    if (error.code === "23505") throw new TaskCatalogDuplicateError(null);
    throw error;
  }
  return data;
}

export async function updateCatalogTask(
  actor,
  taskCatalogId,
  { title, description = "", durationMinutes },
  { existing } = {},
) {
  assertCan(actor, CAPABILITY.TASK_CATALOG_EDIT, {}, undefined, EDIT_DENIED);
  checkInput({ title, description, durationMinutes: durationMinutes ?? null });
  const organizationId = await requireOrg(actor);
  const known = existing || (await loadTaskCatalog(actor));
  const duplicate = findCatalogDuplicate(known, title, { excludeId: taskCatalogId });
  if (duplicate) throw new TaskCatalogDuplicateError(duplicate);

  const { data, error } = await supabase
    .from("task_catalog")
    .update({
      title: normalizeTitle(title),
      description: String(description || "").trim() || null,
      ...(durationMinutes !== undefined ? { planned_duration_minutes: durationMinutes || null } : {}),
    })
    .eq("task_catalog_id", taskCatalogId)
    .eq("organization_id", organizationId)
    .select("*")
    .single();
  if (error) {
    if (error.code === "23505") throw new TaskCatalogDuplicateError(null);
    throw error;
  }
  return data;
}

export async function setCatalogTaskActive(actor, taskCatalogId, isActive) {
  assertCan(actor, CAPABILITY.TASK_CATALOG_EDIT, {}, undefined, EDIT_DENIED);
  const organizationId = await requireOrg(actor);
  const { data, error } = await supabase
    .from("task_catalog")
    .update({ is_active: Boolean(isActive) })
    .eq("task_catalog_id", taskCatalogId)
    .eq("organization_id", organizationId)
    .select("*")
    .single();
  if (error) throw error;
  return data;
}
